import type { CreateWorkfrontConfig } from '@wf-coe/types';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useApiClient } from '../lib/use-api-client.js';
import type { WorkfrontConfigDto } from './use-workfront-configs.js';

interface CreateWorkfrontConfigInput {
  clientId: string;
  body: CreateWorkfrontConfig;
}

/**
 * Mutation hook for `POST /v1/clients/{clientId}/workfront-configs`.
 * Invalidates the per-tenant list cache on success.
 */
export function useCreateWorkfrontConfigMutation(): UseMutationResult<
  WorkfrontConfigDto,
  Error,
  CreateWorkfrontConfigInput
> {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, body }: CreateWorkfrontConfigInput) => {
      if (!api) throw new Error('useCreateWorkfrontConfigMutation: api client unavailable');
      return api.post<WorkfrontConfigDto>(`/v1/clients/${clientId}/workfront-configs`, body);
    },
    onSuccess: (_data, { clientId }) => {
      void queryClient.invalidateQueries({ queryKey: ['workfront-configs', clientId] });
    },
  });
}
